// frontend/src/modules/invoicing/pages/InvoiceEditorPage.tsx
import * as React from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { Save, ArrowLeft, X } from 'lucide-react';
import { PageHeader } from '../../../design-system/components/PageHeader';
import { LoadingState } from '../../../design-system/components/LoadingState';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Textarea } from '../../../components/ui/textarea';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../../../components/ui/select';
import { useApi } from '../../../design-system/useApi';
import { useBranding } from '../../../core/branding/BrandingProvider';
import { ClientPicker } from '../components/ClientPicker';
import { InvoiceLineItemsEditor } from '../components/InvoiceLineItemsEditor';
import { InvoiceLivePreview } from '../components/InvoiceLivePreview';
import { invoicesApi, settingsApi } from '../api';
import { VISUAL } from '../../../design-system/visual';
import type { Client, CreateInvoiceInput, CreateInvoiceItemInput, InvoiceWithItems, TaxProfile } from '../types';

function todayISO(): string { return new Date().toISOString().slice(0, 10); }
function dueDateISO(days: number): string {
  const d = new Date();
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
}

export function InvoiceEditorPage() {
  const { id } = useParams<{ id: string }>();
  const mode = id ? 'edit' : 'new';
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { tenant } = useBranding();
  const tenantCurrency = (tenant as any)?.currency ?? 'USD';
  const defaultDueDays = tenant?.default_invoice_due_days ?? 14;
  const defaultTaxRate = tenant ? Number(tenant.default_tax_rate) : 0;
  // ?type=estimate switches the editor to estimate mode for new documents.
  const newDocType: 'invoice' | 'estimate' = params.get('type') === 'estimate' ? 'estimate' : 'invoice';

  const { data, loading } = useApi<{ invoice: InvoiceWithItems }>(
    mode === 'edit' && id ? `/api/invoicing/invoices/${id}` : null,
  );

  const [form, setForm] = React.useState<CreateInvoiceInput>({
    client_id: params.get('client') ?? '',
    issued_at: todayISO(),
    due_at: dueDateISO(defaultDueDays),
    notes: '',
    terms: '',
    items: [{ description: '', quantity: 1, unit_price: 0, tax_rate: defaultTaxRate }],
    document_type: newDocType,
    discount_type: null,
    discount_value: null,
    tax_profile_id: null,
  });
  const [saving, setSaving] = React.useState(false);

  // Prefill Terms from the company default on NEW INVOICES only (never
  // estimates — company default terms are an invoices-only contract), and
  // only until the user starts typing into the field themselves — a ref
  // (not state) so the touched flag survives without retriggering this
  // effect.
  const termsTouchedRef = React.useRef(false);
  React.useEffect(() => {
    if (mode !== 'new' || newDocType !== 'invoice') return;
    let cancelled = false;
    settingsApi.get()
      .then((settings) => {
        if (cancelled || termsTouchedRef.current) return;
        setForm((f) => (f.terms ? f : { ...f, terms: settings.invoice_default_terms ?? '' }));
      })
      .catch(() => { /* no default available — leave terms blank */ });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, newDocType]);

  // Tax profiles (036): picker source + default preselect on new documents.
  const taxProfilesQ = useApi<{ tax_profiles: TaxProfile[] }>('/api/invoicing/tax-profiles');
  const taxProfiles = React.useMemo(() => taxProfilesQ.data?.tax_profiles ?? [], [taxProfilesQ.data]);
  const defaultProfileApplied = React.useRef(false);
  React.useEffect(() => {
    if (mode !== 'new' || defaultProfileApplied.current || taxProfiles.length === 0) return;
    const def = taxProfiles.find((p) => p.is_default);
    if (def) {
      defaultProfileApplied.current = true;
      setForm((f) => (f.tax_profile_id ? f : { ...f, tax_profile_id: def.id }));
    }
  }, [taxProfiles, mode]);

  React.useEffect(() => {
    if (mode !== 'new' || !tenant) return;
    setForm((f) => ({
      ...f,
      due_at: f.due_at || dueDateISO(tenant.default_invoice_due_days),
      items: f.items.length === 1 && !f.items[0].description
        ? [{ description: '', quantity: 1, unit_price: 0, tax_rate: Number(tenant.default_tax_rate) }]
        : f.items,
    }));
  }, [tenant, mode]);

  React.useEffect(() => {
    if (mode === 'edit' && data?.invoice) {
      const inv = data.invoice;
      setForm({
        client_id: inv.client_id,
        issued_at: inv.issued_at.slice(0, 10),
        due_at: inv.due_at.slice(0, 10),
        notes: inv.notes ?? '',
        terms: inv.terms ?? '',
        items: inv.items.map((it): CreateInvoiceItemInput => ({
          service_id: it.service_id,
          description: it.description,
          quantity: Number(it.quantity),
          unit_price: Number(it.unit_price),
          tax_rate: Number(it.tax_rate),
          discount_percent: it.discount_percent != null ? Number(it.discount_percent) : null,
        })),
        document_type: inv.document_type === 'estimate' ? 'estimate' : 'invoice',
        discount_type: inv.discount_type ?? null,
        discount_value: inv.discount_value != null ? Number(inv.discount_value) : null,
        tax_profile_id: inv.tax_profile_id ?? null,
      });
    }
  }, [data, mode]);

  // Pull the selected client's details for the live preview + currency.
  const clientQ = useApi<{ client: Client }>(form.client_id ? `/api/invoicing/clients/${form.client_id}` : null, [form.client_id]);
  const currency = clientQ.data?.client?.currency ?? (mode === 'edit' ? data?.invoice?.currency : null) ?? tenantCurrency;

  const selectedProfile = form.tax_profile_id ? taxProfiles.find((p) => p.id === form.tax_profile_id) ?? null : null;
  const taxRateOverride = selectedProfile ? Number(selectedProfile.rate_percent) : null;
  const isEstimate = form.document_type === 'estimate';
  const docLabel = isEstimate ? 'estimate' : 'invoice';

  async function save(e: React.FormEvent) {
    e.preventDefault();
    if (!form.client_id) { toast.error('Pick a client'); return; }
    if (!form.due_at) { toast.error('Due date required'); return; }
    if (form.items.length === 0) { toast.error('Add at least one line'); return; }
    setSaving(true);
    try {
      if (mode === 'edit' && id) {
        const inv = await invoicesApi.update(id, form);
        toast.success(`${isEstimate ? 'Estimate' : 'Invoice'} ${inv.invoice_number} updated`);
        navigate(`/dashboard/invoices/${inv.id}`);
      } else {
        const inv = await invoicesApi.create(form);
        toast.success(`${isEstimate ? 'Estimate' : 'Invoice'} ${inv.invoice_number} created`);
        navigate(`/dashboard/invoices/${inv.id}`);
      }
    } catch (err: any) {
      toast.error('Save failed', { description: err.message });
    } finally {
      setSaving(false);
    }
  }

  if (mode === 'edit' && loading) return <LoadingState variant="page" />;
  if (mode === 'edit' && data && data.invoice.status !== 'draft') {
    return (
      <div className="px-10 py-10">
        <p className="text-[14px]">
          This {data.invoice.document_type === 'estimate' ? 'estimate' : 'invoice'} is <strong>{data.invoice.status}</strong> and can't be edited.{' '}
          <Link to={`/dashboard/invoices/${id}`} className="underline hover:text-saffron-foreground">Back to detail</Link>.
        </p>
      </div>
    );
  }

  const cancelHref = mode === 'edit' && id ? `/dashboard/invoices/${id}` : '/dashboard/invoices';

  return (
    <form onSubmit={save} className="flex flex-col min-h-[calc(100vh-4rem)]">
      <PageHeader
        eyebrow={<><Link to="/dashboard/invoices" className="hover:text-foreground transition-colors">Invoices</Link> / {mode === 'edit' ? data?.invoice?.invoice_number : (isEstimate ? 'New estimate' : 'New')}</>}
        title={mode === 'edit' ? `Edit ${data?.invoice?.invoice_number ?? docLabel}` : (isEstimate ? 'Compose estimate' : 'Compose invoice')}
        description={mode === 'edit'
          ? 'Adjust line items, dates, discounts, notes. Total updates as you type.'
          : isEstimate
            ? 'Quote the work first. Once the client accepts, convert it to an invoice in one click.'
            : 'Pick a client, drop in line items, hit send. Preview on the right updates as you type.'}
        actions={
          <div className="flex items-center gap-2">
            <Link to={cancelHref}>
              <Button variant="outline" type="button" className="rounded-full h-10 px-4 text-[13px] font-semibold gap-1.5">
                <X className="h-3.5 w-3.5" />
                Cancel
              </Button>
            </Link>
            <Button type="submit" disabled={saving} className="rounded-full h-10 px-5 text-[13px] font-semibold gap-1.5">
              <Save className="h-3.5 w-3.5" />
              {saving ? 'Saving…' : (mode === 'edit' ? 'Save changes' : 'Create draft')}
            </Button>
          </div>
        }
      />

      <div className="flex-1 px-6 sm:px-8 lg:px-10 pb-14">
        <div className="grid grid-cols-1 xl:grid-cols-[1fr_minmax(0,520px)] gap-6 xl:gap-8 items-start">
          {/* LEFT — form */}
          <div className="space-y-6 min-w-0">
            <section className={[VISUAL.bento, 'p-6 sm:p-7'].join(' ')}>
              <h3 className={[VISUAL.dashSection, 'mb-5'].join(' ')}>Customer & dates</h3>
              <div className="grid grid-cols-1 sm:grid-cols-[1fr_180px_180px] gap-4">
                <div className="space-y-1.5">
                  <Label>Client<span className="text-destructive ml-0.5">*</span></Label>
                  <ClientPicker value={form.client_id || null} onChange={(c) => setForm({ ...form, client_id: c })} />
                  {clientQ.data?.client?.currency && (
                    <p className="text-[12px] text-muted-foreground">
                      Bills in <span className="font-mono font-semibold">{clientQ.data.client.currency}</span> (client currency)
                    </p>
                  )}
                </div>
                <div className="space-y-1.5">
                  <Label>Issued</Label>
                  <Input type="date" value={form.issued_at ?? ''} onChange={(e) => setForm({ ...form, issued_at: e.target.value })} />
                </div>
                <div className="space-y-1.5">
                  <Label>{isEstimate ? 'Valid until' : 'Due'}<span className="text-destructive ml-0.5">*</span></Label>
                  <Input type="date" value={form.due_at} onChange={(e) => setForm({ ...form, due_at: e.target.value })} required />
                </div>
              </div>
            </section>

            <section className={[VISUAL.bento, 'p-6 sm:p-7'].join(' ')}>
              <h3 className={[VISUAL.dashSection, 'mb-5'].join(' ')}>Line items</h3>
              <InvoiceLineItemsEditor
                items={form.items}
                onChange={(items) => setForm({ ...form, items })}
                currency={currency}
                invoiceDiscount={form.discount_type && Number(form.discount_value) > 0
                  ? { type: form.discount_type, value: Number(form.discount_value) }
                  : null}
                taxRateOverride={taxRateOverride}
              />
            </section>

            <section className={[VISUAL.bento, 'p-6 sm:p-7'].join(' ')}>
              <h3 className={[VISUAL.dashSection, 'mb-5'].join(' ')}>Discount & tax</h3>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div className="space-y-1.5">
                  <Label>{isEstimate ? 'Estimate' : 'Invoice'} discount</Label>
                  <Select
                    value={form.discount_type ?? 'none'}
                    onValueChange={(v) => setForm({
                      ...form,
                      discount_type: v === 'none' ? null : (v as 'percent' | 'fixed'),
                      discount_value: v === 'none' ? null : (form.discount_value ?? 0),
                    })}
                  >
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">No discount</SelectItem>
                      <SelectItem value="percent">Percent of subtotal</SelectItem>
                      <SelectItem value="fixed">Fixed amount</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label>{form.discount_type === 'percent' ? 'Discount (%)' : `Discount (${currency})`}</Label>
                  <Input
                    type="number" min="0" step="0.01"
                    max={form.discount_type === 'percent' ? 100 : undefined}
                    disabled={!form.discount_type}
                    value={form.discount_value ?? ''}
                    onChange={(e) => setForm({ ...form, discount_value: e.target.value === '' ? null : Number(e.target.value) })}
                  />
                  <p className="text-[12px] text-muted-foreground">Applied after line discounts, before tax.</p>
                </div>
                <div className="space-y-1.5">
                  <Label>Tax profile</Label>
                  <Select
                    value={form.tax_profile_id ?? 'none'}
                    onValueChange={(v) => setForm({ ...form, tax_profile_id: v === 'none' ? null : v })}
                  >
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Per-line rates</SelectItem>
                      {taxProfiles.map((p) => (
                        <SelectItem key={p.id} value={p.id}>{p.name} — {Number(p.rate_percent)}%</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-[12px] text-muted-foreground">
                    {selectedProfile
                      ? `Overrides every line at ${Number(selectedProfile.rate_percent)}%.`
                      : 'Manage profiles under Settings → Invoicing.'}
                  </p>
                </div>
              </div>
            </section>

            <section className={[VISUAL.bento, 'p-6 sm:p-7'].join(' ')}>
              <h3 className={[VISUAL.dashSection, 'mb-5'].join(' ')}>Notes & terms</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <Label>Notes</Label>
                  <Textarea
                    rows={4}
                    value={form.notes ?? ''}
                    onChange={(e) => setForm({ ...form, notes: e.target.value })}
                    placeholder="Thank-you note, payment instructions, anything that helps the client."
                  />
                </div>
                <div className="space-y-1.5">
                  <Label>Terms</Label>
                  <Textarea
                    rows={4}
                    value={form.terms ?? ''}
                    onChange={(e) => { termsTouchedRef.current = true; setForm({ ...form, terms: e.target.value }); }}
                    placeholder="Net 30, late fees, refund policy."
                  />
                </div>
              </div>
            </section>

            <div className="flex items-center justify-between pt-2">
              <Link to={cancelHref} className="inline-flex items-center gap-1.5 text-[13px] font-semibold text-muted-foreground hover:text-foreground transition-colors">
                <ArrowLeft className="h-3.5 w-3.5" />
                Back without saving
              </Link>
              <Button type="submit" disabled={saving} className="rounded-full h-11 px-6 text-[14px] font-semibold gap-1.5">
                <Save className="h-3.5 w-3.5" />
                {saving ? 'Saving…' : (mode === 'edit' ? 'Save changes' : 'Create draft')}
              </Button>
            </div>
          </div>

          {/* RIGHT — sticky live preview */}
          <aside className="xl:sticky xl:top-[calc(64px+1.5rem)] self-start min-w-0">
            <p className="text-[11px] uppercase tracking-[0.16em] font-semibold text-muted-foreground/80 mb-3">Live preview</p>
            <InvoiceLivePreview
              form={form}
              clientName={clientQ.data?.client?.name}
              clientEmail={clientQ.data?.client?.email ?? undefined}
              clientAddress={clientQ.data?.client?.address ?? undefined}
              invoiceNumber={data?.invoice?.invoice_number}
              currency={currency}
              taxRateOverride={taxRateOverride}
            />
            <p className="text-center text-[11px] text-muted-foreground/70 mt-3">
              The real PDF is regenerated on save — this preview is "good enough" for layout + totals.
            </p>
          </aside>
        </div>
      </div>
    </form>
  );
}
